/**
 * A connection to somebody else's SaaS - DATA-011.
 *
 * The shape is `docs/architecture/data-model-execution.md` §16.6. What this
 * file adds is the three vocabularies that document leaves open, declared once
 * as `as const` arrays so the CHECK constraints in `0005_connection`, the row
 * decoder and the wire contract cannot drift - the same arrangement
 * `conversation.ts` uses for its providers.
 *
 * **We hold no SaaS credential.** ADR-0026: "Pipedream-managed OAuth refresh
 * tokens are not stored by us at all." What a row carries is the provider's own
 * id for the connection, plus enough metadata to show a person which account
 * they attached and whether it still works. `secret_ref` exists for a future
 * native provider whose token we would hold ourselves, encrypted; it stays null
 * for everything in this batch, and nothing in it builds envelope encryption.
 */

/**
 * Who the connection belongs to.
 *
 * `owner_id` means a different table for each: an `app_user` for `USER`, the
 * workspace itself for `WORKSPACE`, and nothing at all for `SERVICE`. That is
 * why the column carries no foreign key - a single column cannot reference
 * three tables - and why the ownership *rules* are a separate ticket (INT-021,
 * INT-022, both P1). This batch stores the fact and enforces nothing beyond the
 * workspace.
 */
export const CONNECTION_OWNER_TYPES = ['USER', 'WORKSPACE', 'SERVICE'] as const;
export type ConnectionOwnerType = (typeof CONNECTION_OWNER_TYPES)[number];

/**
 * Who holds the credential.
 *
 * `NATIVE` is in the vocabulary although nothing implements it, for the reason
 * `0003_conversation` gave for listing every channel provider: ADR-0001 says
 * the canonical capability is what a task stores and the provider binding is
 * resolved at runtime, which is only true if a second provider can arrive
 * without a migration. A vocabulary widened by migration is one somebody widens
 * in a hurry.
 */
export const CONNECTION_PROVIDERS = ['PIPEDREAM', 'NATIVE'] as const;
export type ConnectionProvider = (typeof CONNECTION_PROVIDERS)[number];

/**
 * Whether the connection can be used right now.
 *
 * `REAUTH_REQUIRED` is not a failure state and not the same as `REVOKED`:
 * `docs/architecture/pipedream-connect.md` records that adding an action can
 * require a new OAuth scope, so a working connection can need re-consent
 * without anybody having disconnected anything. INT-012 owns the re-consent
 * flow; this is the state it will read.
 *
 * `PENDING` is a connection whose row exists before the person has finished at
 * the provider. `REVOKED` is terminal here - the row is kept rather than
 * deleted, because a tool execution that referenced it should still be
 * explicable afterwards.
 */
export const CONNECTION_STATUSES = ['PENDING', 'AVAILABLE', 'REAUTH_REQUIRED', 'REVOKED'] as const;
export type ConnectionStatus = (typeof CONNECTION_STATUSES)[number];

/**
 * The connection as the domain sees it.
 *
 * Hand-written rather than inferred from a zod schema, which is this package's
 * rule: a wire shape is checked on arrival and a domain shape is not, so one is
 * zod and one is not. `http/integration.ts` holds the wire half, and
 * `connection.spec.ts` asserts the two agree - in both directions and with
 * optionality flattened, because a type missing an optional property is
 * assignable either way and five of the twelve fields here are optional.
 */
export interface Connection {
  readonly id: string;
  readonly workspaceId: string;
  readonly ownerType: ConnectionOwnerType;
  /** Absent for `SERVICE`, which belongs to no one in particular. */
  readonly ownerId?: string;
  /** The app this connects to, in the provider's vocabulary: `hubspot`, `slack`. */
  readonly appKey: string;
  readonly provider: ConnectionProvider;
  /** The provider's own id. Absent until the person finishes at the provider. */
  readonly providerConnectionId?: string;
  /** What to call it on screen. Falls back to the app key when the provider offers nothing. */
  readonly displayName: string;
  /** The account at the far end - an email, a team name. Absent until known. */
  readonly externalAccountId?: string;
  readonly scopes: readonly string[];
  readonly status: ConnectionStatus;
  /** When the connection was last known to work. Absent until it has been. */
  readonly lastVerifiedAt?: string;
  readonly createdAt: string;
}
